import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Loader2, Send, Mail } from "lucide-react";
import { useI18n } from "../i18n";

export const ContactSection = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [news, setNews] = useState("");
  const [newsLoading, setNewsLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("con.toast_sent"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(t("con.toast_senderr"));
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    setNewsLoading(true);
    try {
      const r = await api.post("/newsletter", { email: news });
      toast.success(r.data.message);
      setNews("");
    } catch {
      toast.error(t("con.toast_nlerr"));
    } finally {
      setNewsLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E1000F] mb-4">{t("con.eyebrow")}</p>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl text-[#0B1121] leading-[0.95]">
            {t("con.title")}
          </h2>
          <p className="mt-6 text-lg text-slate-600 max-w-md">{t("con.desc")}</p>
          <a
            href="mailto:president@reset-relance.org"
            data-testid="contact-email-link"
            className="mt-5 inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-[#F8F9FA] px-5 py-4 hover:border-[#001489] transition-colors group"
          >
            <span className="w-10 h-10 rounded-xl bg-[#001489] flex items-center justify-center shrink-0">
              <Mail className="text-white" size={18} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-mono-accent text-[10px] uppercase tracking-widest text-slate-500">{t("con.cpLabel")}</span>
              <span className="font-display font-extrabold text-[#0B1121] group-hover:text-[#001489] transition-colors">president@reset-relance.org</span>
            </span>
          </a>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input required data-testid="contact-name" className="h-12" placeholder={t("con.name")}
                value={form.name} onChange={(e) => set("name", e.target.value)} />
              <Input required type="email" data-testid="contact-email" className="h-12" placeholder={t("con.email")}
                value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <Input required data-testid="contact-subject" className="h-12" placeholder={t("con.subject")}
              value={form.subject} onChange={(e) => set("subject", e.target.value)} />
            <Textarea required data-testid="contact-message" rows={5} placeholder={t("con.message")}
              value={form.message} onChange={(e) => set("message", e.target.value)} />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-form-submit"
              className="inline-flex items-center gap-2 bg-[#0B1121] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#001489] transition-all disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <><Send size={18} /> {t("con.submit")}</>}
            </button>
          </form>
        </div>

        <div className="lg:col-span-6">
          <div className="bg-[#0B1121] rounded-3xl p-8 sm:p-10 h-full flex flex-col justify-center relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-[0.12]" />
            <div className="relative">
              <Mail className="text-[#38BDF8]" size={32} />
              <h3 className="mt-5 font-display font-black tracking-tighter text-3xl text-white leading-tight">
                {t("con.nl_title")}
              </h3>
              <p className="mt-3 text-white/70">{t("con.nl_desc")}</p>
              <form onSubmit={subscribe} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
                <Input required type="email" data-testid="newsletter-email"
                  className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  placeholder={t("con.nl_ph")} value={news} onChange={(e) => setNews(e.target.value)} />
                <button
                  type="submit"
                  disabled={newsLoading}
                  data-testid="newsletter-submit"
                  className="h-12 px-6 bg-[#E1000F] text-white font-semibold rounded-lg hover:bg-[#c1000d] transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {newsLoading ? <Loader2 className="animate-spin mx-auto" size={18} /> : t("con.nl_submit")}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
